import { getQuestionType } from '@exam-management/common/utils';
import { QuestionTypeChip } from '@exam-management/components';
import { QuestionGroupType, QuestionStatus } from '@exam-management/graphql';
import { Box, Flex, Text } from '@shared/components';

import { UpdateQuestionStatusListDataGrid } from '../../components/update-question-status-list-data-grid';
import { useQuestionReviewTaskDetails } from '../../providers';
import { type QuestionReviewUpdateQuestionStatusRow } from '../../types';
import { sortQuestionByRefCode } from '../../utils';

import columns from './columns';
import itemSetColumns from './item-set-columns';

export default function UpdateQuestionStatusList() {
  const { questions } = useQuestionReviewTaskDetails();

  const questionSingleType = getQuestionType(QuestionGroupType.Single);
  const questionItemSetType = getQuestionType(QuestionGroupType.ItemSet);

  const singleQuestions: QuestionReviewUpdateQuestionStatusRow[] = [...questions]
    .filter((q) => q.questionGroup?.type === QuestionGroupType.Single)
    .sort(sortQuestionByRefCode)
    .map((question, index) => ({
      id: question.id,
      qid: question.refCode,
      order: index + 1,
      status: question.status,
    }));

  const itemSetGroupMap = new Map<
    string,
    {
      groupRefCode: string;
      questionIds: string[];
      status: (typeof questions)[number]['status'];
      totalQuestions: number;
      name: string;
    }
  >();

  for (const question of [...questions]
    .filter((q) => q.questionGroup?.type === QuestionGroupType.ItemSet)
    .sort(sortQuestionByRefCode)) {
    const groupId = question.questionGroup?.id;
    if (!groupId) continue;

    const existing = itemSetGroupMap.get(groupId);
    if (existing) {
      existing.questionIds.push(question.id);
    } else {
      itemSetGroupMap.set(groupId, {
        groupRefCode: question.questionGroup?.refCode ?? question.refCode,
        questionIds: [question.id],
        status: QuestionStatus.Draft,
        totalQuestions: question.questionGroup?.totalQuestions ?? 0,
        name: question.questionGroup?.name ?? '',
      });
    }
  }

  const itemSetQuestions: QuestionReviewUpdateQuestionStatusRow[] = Array.from(
    itemSetGroupMap.entries(),
  ).map(([groupId, group], index) => ({
    id: groupId,
    qid: group.groupRefCode,
    order: index + 1,
    status: group.status,
    questionIds: group.questionIds,
    itemSetType: `Item Set: ${group.totalQuestions}Q`,
    itemSetName: group.name,
  }));

  return (
    <Box>
      {singleQuestions.length > 0 ? (
        <>
          <Flex
            sx={(theme) => ({ alignItems: 'center', gap: theme.spacing(2), mb: theme.spacing(4) })}
          >
            <Text variant='body2'>Question Type:</Text>
            <QuestionTypeChip type={questionSingleType} />
          </Flex>
          <UpdateQuestionStatusListDataGrid columns={columns} rows={singleQuestions} />
        </>
      ) : null}
      {itemSetQuestions.length > 0 ? (
        <>
          <Flex
            sx={(theme) => ({
              alignItems: 'center',
              gap: theme.spacing(2),
              ...(singleQuestions.length > 0 ? { my: theme.spacing(4) } : { mb: theme.spacing(4) }),
            })}
          >
            <Text variant='body2'>Question Type:</Text>
            <QuestionTypeChip type={questionItemSetType} />
          </Flex>
          <UpdateQuestionStatusListDataGrid columns={itemSetColumns} rows={itemSetQuestions} />
        </>
      ) : null}
    </Box>
  );
}
